import { useState } from 'react';
import { useOutshipments, useCreateOutshipment, useUpdateOutshipment, useSubmitOutshipment } from '../hooks/useOutshipments';
import { useShippingCodes } from '../hooks/useShippingCodes';
import { useAddresses } from '../hooks/useAddresses';
import { useAccounts } from '../hooks/useAccounts';
import { useProducts } from '../hooks/useProducts';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createOutshipmentSchema, type Outshipment, type CreateOutshipmentInput, type Address, type ShippingCode, type Product, type Account } from '@aigftpwatch/shared';
import { Table } from '../components/ui/Table';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import { PlusIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

export function OutshipmentPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const { data, isLoading } = useOutshipments(statusFilter || undefined, page);
  const { data: codes } = useShippingCodes();
  const { data: addresses } = useAddresses();
  const { data: accounts } = useAccounts();
  const { data: products } = useProducts();
  const createMutation = useCreateOutshipment();
  const updateMutation = useUpdateOutshipment();
  const submitMutation = useSubmitOutshipment();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Outshipment | null>(null);

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<CreateOutshipmentInput>({
    resolver: zodResolver(createOutshipmentSchema) as never,
  });

  function openNew() {
    setEditing(null);
    reset({
      shipping_code: '', product_code: '', quantity: 1,
      ship_to_name: '', ship_to_name2: '', ship_to_address1: '', ship_to_address2: '',
      ship_to_city: '', ship_to_state: '', ship_to_zip: '', ship_to_country: 'US',
    });
    setModalOpen(true);
  }

  function openEdit(row: Outshipment) {
    if (row.status !== 'draft') return;
    setEditing(row);
    reset({
      shipping_code: row.shipping_code, product_code: row.product_code,
      quantity: row.quantity, delivery_no: row.delivery_no ?? '',
      ship_to_name: row.ship_to_name, ship_to_name2: row.ship_to_name2 ?? '',
      ship_to_address1: row.ship_to_address1, ship_to_address2: row.ship_to_address2 ?? '',
      ship_to_city: row.ship_to_city, ship_to_state: row.ship_to_state,
      ship_to_zip: row.ship_to_zip, ship_to_country: row.ship_to_country ?? 'US',
    });
    setModalOpen(true);
  }

  function fillFromAddress(addressId: string) {
    const addr = (addresses ?? []).find((a: Address) => String(a.id) === addressId);
    if (!addr) return;
    setValue('ship_to_name', addr.name);
    setValue('ship_to_name2', addr.name2 ?? '');
    setValue('ship_to_address1', addr.address1);
    setValue('ship_to_address2', addr.address2 ?? '');
    setValue('ship_to_city', addr.city);
    setValue('ship_to_state', addr.state);
    setValue('ship_to_zip', addr.zip);
    setValue('ship_to_country', addr.country ?? 'US');
  }

  async function onSubmit(formData: CreateOutshipmentInput) {
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, ...formData });
        toast.success('Outshipment updated');
      } else {
        await createMutation.mutateAsync(formData);
        toast.success('Outshipment draft created');
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    }
  }

  async function handleSubmitToSFTP(id: number, e: React.MouseEvent) {
    e.stopPropagation();
    if (!confirm('Generate Excel and upload to FideliTrade SFTP?')) return;
    try {
      await submitMutation.mutateAsync(id);
      toast.success('Outshipment submitted to SFTP');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit');
    }
  }

  const outshipments = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <p className="text-sm text-silver-500">{pagination?.total ?? 0} outshipments</p>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="rounded-lg border border-silver-300 px-3 py-1.5 text-sm">
            <option value="">All Statuses</option>
            {['draft', 'uploaded', 'processing', 'completed', 'rejected', 'cancelled'].map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
        <Button onClick={openNew}><PlusIcon className="h-4 w-4" /> New Outshipment</Button>
      </div>

      <Card padding={false}>
        <Table<Outshipment>
          loading={isLoading}
          data={outshipments}
          onRowClick={openEdit}
          columns={[
            { key: 'id', header: 'ID' },
            { key: 'product_code', header: 'Product' },
            { key: 'quantity', header: 'Qty' },
            { key: 'ship_to_name', header: 'Ship To' },
            { key: 'ship_to_city', header: 'City' },
            { key: 'shipping_code', header: 'Method' },
            { key: 'status', header: 'Status', render: (r) => <Badge>{r.status}</Badge> },
            {
              key: 'actions', header: '',
              render: (r) => r.status === 'draft' ? (
                <Button size="sm" onClick={(e) => handleSubmitToSFTP(r.id, e)} loading={submitMutation.isPending}>
                  <PaperAirplaneIcon className="h-4 w-4" /> Submit
                </Button>
              ) : null,
            },
          ]}
        />
        {pagination && (
          <div className="px-4">
            <Pagination page={page} totalPages={pagination.total_pages} onPageChange={setPage} />
          </div>
        )}
      </Card>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Outshipment' : 'New Outshipment'} size="xl">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Address book quick-fill */}
          <div className="rounded-lg border border-gold-200 bg-gold-50 p-3">
            <label className="mb-1 block text-xs font-medium text-gold-700">Fill from Address Book</label>
            <select onChange={(e) => fillFromAddress(e.target.value)} className="w-full rounded-lg border border-gold-300 px-3 py-2 text-sm">
              <option value="">Select saved address...</option>
              {(addresses ?? []).map((a: Address) => (
                <option key={a.id} value={String(a.id)}>{a.label} - {a.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Select label="Shipping Method" options={(codes ?? []).map((c: ShippingCode) => ({
              value: c.code, label: `${c.code} - ${c.description}`,
            }))} placeholder="Select shipping code..." {...register('shipping_code')} error={errors.shipping_code?.message} />
            <Select label="Product" options={(products ?? []).map((p: Product) => ({
              value: p.product_code, label: `${p.product_code} - ${p.description}`,
            }))} placeholder="Select product..." {...register('product_code')} error={errors.product_code?.message} />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Input label="Quantity" type="number" step="any" {...register('quantity', { valueAsNumber: true })} error={errors.quantity?.message} />
            <Input label="Delivery No" {...register('delivery_no')} />
            <Select label="From Account" options={(accounts ?? []).map((a: Account) => ({
              value: String(a.id), label: `${a.account_number} - ${a.title}`,
            }))} placeholder="Select account..." {...register('from_account_id', { valueAsNumber: true })} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input label="Ship To Name" {...register('ship_to_name')} error={errors.ship_to_name?.message} />
            <Input label="Ship To Name 2" {...register('ship_to_name2')} />
          </div>
          <Input label="Address 1" {...register('ship_to_address1')} error={errors.ship_to_address1?.message} />
          <Input label="Address 2" {...register('ship_to_address2')} />
          <div className="grid grid-cols-4 gap-4">
            <Input label="City" {...register('ship_to_city')} error={errors.ship_to_city?.message} />
            <Input label="State" {...register('ship_to_state')} error={errors.ship_to_state?.message} />
            <Input label="ZIP" {...register('ship_to_zip')} error={errors.ship_to_zip?.message} />
            <Input label="Country" {...register('ship_to_country')} />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
              {editing ? 'Update Draft' : 'Save Draft'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
